import { useState, useEffect } from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { useCronJob } from '../../../apps/hooks/system/useCronJob';
import { HTTPMethod } from '../../../models/CronJob';
import { CronJobFilterStatus } from '../../../infrastructure/CronJobClient';
import type { HTTPCronJob, CronJobStatus } from '../../../models/CronJob';
import type { FormData } from './components';
import {
  CronJobList,
  CronJobFilters,
  CronJobDetailModal,
  CreateCronJobModal,
  UpdateCronJobModal,
  DeleteCronJobModal,
} from './components';

export default function CronJobPage() {
  const {
    listCronJobsWithFilter,
    createCronJobMutation,
    updateCronJobMutation,
    updateCronJobStatusMutation,
    deleteCronJobMutation,
  } = useCronJob();

  // Filter state
  const [selectedStatus, setSelectedStatus] = useState<CronJobFilterStatus>(CronJobFilterStatus.ENABLED);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Selected cron job state
  const [selectedCronJob, setSelectedCronJob] = useState<HTTPCronJob | null>(null);

  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch cron jobs with current filter
  const cronJobsQuery = listCronJobsWithFilter({
    status: selectedStatus,
    limit: 100,
  });

  const { data: cronJobData, isLoading, refetch } = cronJobsQuery;
  const cronJobs = cronJobData?.cronJobs || [];

  // Clear messages after some time
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [error]);

  // Helper function to convert datetime-local to UTC ISO string
  const convertToUTCISOString = (datetimeLocal: string): string => {
    // datetime-local format: "2025-01-25T15:30"
    // User enters UTC time directly, just add seconds and timezone
    return datetimeLocal + ':00.000Z';
  };

  // Helper function to validate if datetime is in the future (UTC)
  const isValidFutureTime = (datetimeLocal: string): boolean => {
    const utcDateTime = new Date(datetimeLocal + ':00.000Z');
    const nowUTC = new Date();
    return utcDateTime > nowUTC;
  };

  // Event handlers
  const handleCreateCronJob = async (formData: FormData) => {
    try {
      setError(null);

      // Parse body if it's a string
      let parsedBody = formData.body;
      if (formData.body && formData.method !== HTTPMethod.GET) {
        try {
          parsedBody = JSON.parse(formData.body);
        } catch {
          // Keep as string if it's not valid JSON
        }
      }

      // For one-time execution, store ISO string directly
      let cronExpression = formData.cronExpression;
      if (formData.isOneTime && formData.scheduledDateTime) {
        // Validate future time in UTC
        if (!isValidFutureTime(formData.scheduledDateTime)) {
          throw new Error('Scheduled time must be in the future (UTC)');
        }
        // Convert to UTC ISO string
        cronExpression = convertToUTCISOString(formData.scheduledDateTime);
      }

      await createCronJobMutation.mutateAsync({
        name: formData.name,
        cronExpression,
        status: formData.status,
        command: {
          command: formData.cronJobType,
          header: formData.headers,
          argument: {
            method: formData.method,
            url: formData.url,
            body: parsedBody,
          },
        },
      });

      setShowCreateModal(false);
      setSuccess('CronJob created successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create cron job');
    }
  };

  const handleUpdateCronJob = async (formData: FormData) => {
    if (!selectedCronJob) {
      return;
    }

    try {
      setError(null);

      // Parse body if it's a string
      let parsedBody = formData.body;
      if (formData.body && formData.method !== HTTPMethod.GET) {
        try {
          parsedBody = JSON.parse(formData.body);
        } catch {
          // Keep as string if it's not valid JSON
        }
      }

      // For one-time execution, store ISO string directly
      let cronExpression = formData.cronExpression;
      if (formData.isOneTime && formData.scheduledDateTime) {
        // Validate future time in UTC
        if (!isValidFutureTime(formData.scheduledDateTime)) {
          throw new Error('Scheduled time must be in the future (UTC)');
        }
        // Convert to UTC ISO string
        cronExpression = convertToUTCISOString(formData.scheduledDateTime);
      }

      await updateCronJobMutation.mutateAsync({
        id: selectedCronJob.id,
        request: {
          name: formData.name,
          cronExpression,
          status: formData.status,
          command: {
            command: formData.cronJobType,
            header: formData.headers,
            argument: {
              method: formData.method,
              url: formData.url,
              body: parsedBody,
            },
          },
        },
      });

      setShowUpdateModal(false);
      setSelectedCronJob(null);
      setSuccess('CronJob updated successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update cron job');
    }
  };

  const handleDeleteCronJob = async () => {
    if (!selectedCronJob) {
      return;
    }

    try {
      setError(null);
      await deleteCronJobMutation.mutateAsync(selectedCronJob.id);
      setShowDeleteModal(false);
      setSelectedCronJob(null);
      setSuccess('CronJob deleted successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete cron job');
    }
  };

  const handleStatusChange = async (cronJob: HTTPCronJob, newStatus: CronJobStatus) => {
    try {
      setError(null);
      await updateCronJobStatusMutation.mutateAsync({
        id: cronJob.id,
        status: newStatus,
      });
      setSuccess(`CronJob status updated to ${newStatus}!`);
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update cron job status');
    }
  };

  const handleView = (cronJob: HTTPCronJob) => {
    setSelectedCronJob(cronJob);
    setShowDetailModal(true);
  };

  const handleEdit = (cronJob: HTTPCronJob) => {
    setSelectedCronJob(cronJob);
    setShowUpdateModal(true);
  };

  const handleDelete = (cronJob: HTTPCronJob) => {
    setSelectedCronJob(cronJob);
    setShowDeleteModal(true);
  };

  const handleEditFromDetail = () => {
    setShowDetailModal(false);
    setShowUpdateModal(true);
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>CronJob Management</h2>
        <Button variant="primary" onClick={() => setShowCreateModal(true)} disabled={createCronJobMutation.isPending}>
          Create Cron Job
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <CronJobFilters
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        totalCronJobs={cronJobs.length}
      />

      <Card>
        <Card.Body>
          <CronJobList
            cronJobs={cronJobs}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            loading={isLoading}
          />
        </Card.Body>
      </Card>

      {/* Create CronJob Modal */}
      <CreateCronJobModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateCronJob}
        isSubmitting={createCronJobMutation.isPending}
      />

      {/* CronJob Detail Modal */}
      <CronJobDetailModal
        show={showDetailModal}
        cronJob={selectedCronJob}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedCronJob(null);
        }}
        onEdit={handleEditFromDetail}
      />

      {/* Update CronJob Modal */}
      <UpdateCronJobModal
        show={showUpdateModal}
        cronJob={selectedCronJob}
        onHide={() => {
          setShowUpdateModal(false);
          setSelectedCronJob(null);
        }}
        onSubmit={handleUpdateCronJob}
        isSubmitting={updateCronJobMutation.isPending}
      />

      {/* Delete CronJob Modal */}
      <DeleteCronJobModal
        show={showDeleteModal}
        cronJob={selectedCronJob}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedCronJob(null);
        }}
        onConfirm={handleDeleteCronJob}
        isDeleting={deleteCronJobMutation.isPending}
      />
    </Container>
  );
}
